import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Field, PremiumBadge, SectionTitle } from "@/components/common";
import { TripForm } from "@/components/TripForm";
import { useLoad, useTrip } from "@/context/TripContext";
import { api, errMsg } from "@/lib/api";

function GuideCard() {
  const { trip, tid, reload, limits } = useTrip();
  const [notes, setNotes] = useState(trip.guide_notes || "");
  const [brand, setBrand] = useState({ brand_color: trip.brand_color || "#F97316", brand_tagline: trip.brand_tagline || "" });
  const url = `${window.location.origin}/g/${trip.guide_slug}`;
  const save = async (body, msg = "Saved") => { try { await api.patch(`/trips/${tid}`, body); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const pro = limits.features.includes("branding");
  return (
    <div className="card space-y-4 p-5" data-testid="guide-settings">
      <div className="flex items-center justify-between"><h3 className="font-bold">Public trip guide</h3><Switch checked={trip.guide_enabled} onCheckedChange={(v) => save({ guide_enabled: v }, v ? "Guide is public" : "Guide is private")} data-testid="guide-toggle" /></div>
      <p className="text-xs text-slate-500">Shows only the trip name, dates, description, these notes and the current itinerary (no prices, people or bookings).</p>
      {trip.guide_enabled && <div className="flex gap-2"><Input readOnly value={url} className="font-mono text-xs" data-testid="guide-url" />
        <Button size="icon" variant="outline" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Copied"); }} aria-label="Copy" data-testid="guide-copy"><Copy className="h-4 w-4" /></Button>
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex h-10 w-10 items-center justify-center rounded-md border" aria-label="Open guide" data-testid="guide-open"><ExternalLink className="h-4 w-4" /></a></div>}
      <Field label="Good-to-know notes" htmlFor="gn"><Textarea id="gn" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Dress code, meeting points, Wi-Fi name (not password)…" data-testid="guide-notes-input" /></Field>
      <Button variant="outline" className="rounded-full" onClick={() => save({ guide_notes: notes })} data-testid="guide-notes-save">Save notes</Button>
      <div className="space-y-2 border-t pt-4"><p className="flex items-center gap-2 text-sm font-semibold">Custom branding <PremiumBadge plan="Pro" /></p>
        <div className="flex gap-2"><Input type="color" disabled={!pro} value={brand.brand_color} onChange={(e) => setBrand({ ...brand, brand_color: e.target.value })} className="w-16" data-testid="brand-color-input" />
          <Input disabled={!pro} placeholder="Tagline, e.g. Maya & Leo · Tulum 2026" value={brand.brand_tagline} onChange={(e) => setBrand({ ...brand, brand_tagline: e.target.value })} data-testid="brand-tagline-input" />
          <Button disabled={!pro} variant="outline" onClick={() => save(brand)} data-testid="brand-save">Save</Button></div></div>
    </div>
  );
}

function Danger() {
  const { trip, tid, isOwner } = useTrip();
  const nav = useNavigate();
  const [name, setName] = useState("");
  if (!isOwner) return null;
  const del = async () => { try { await api.post(`/trips/${tid}/delete`, { confirm_name: name }); toast.success("Trip deleted"); nav("/dashboard"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card space-y-3 border-rose-200 p-5"><h3 className="font-bold text-rose-700">Delete trip</h3>
      <p className="text-sm text-slate-600">Permanently removes the trip and all its data for everyone. Type the trip name to confirm.</p>
      <div className="flex gap-2"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={trip.name} data-testid="delete-trip-confirm" />
        <Button variant="destructive" disabled={name !== trip.name} onClick={del} data-testid="delete-trip-button">Delete</Button></div>
    </div>
  );
}

function CardToggle() {
  const { tid, isOwner } = useTrip();
  const [s, reload] = useLoad(`/trips/${tid}/card-payments`);
  if (!s || !isOwner) return null;
  const set = async (v) => { try { await api.post(`/trips/${tid}/card-payments`, { enabled: v }); toast.success(v ? "Travelers can now pay by card" : "Card payments turned off"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card space-y-2 p-5" data-testid="card-payments-settings">
      <div className="flex items-center justify-between"><h3 className="font-bold">Card payments</h3><Switch checked={s.trip_switch} onCheckedChange={set} disabled={!s.owner_ready && !s.trip_switch} data-testid="card-payments-toggle" /></div>
      <p className="text-xs text-slate-500">Travelers pay their balance by card and the money goes to your Stripe payout account. Triply keeps {s.fee_bps / 100}% on your plan; Stripe's processing fee also applies. Card payments are recorded automatically as "verified by Stripe".</p>
      {!s.owner_ready && <Link to="/billing" className="text-sm font-semibold text-orange-600" data-testid="card-payments-setup-link">Set up payouts first</Link>}
    </div>
  );
}

export default function TripSettings() {
  const { trip, tid, reload } = useTrip();
  const save = async (body) => { await api.patch(`/trips/${tid}`, body); toast.success("Trip updated"); reload(); };
  const setStatus = async (v) => { try { await api.patch(`/trips/${tid}`, { status_override: v }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
      <div className="space-y-4">
        <SectionTitle title="Trip settings" />
        <div className="card p-6"><TripForm initial={trip} onSubmit={save} submitLabel="Save changes" /></div>
      </div>
      <div className="space-y-6 lg:pt-12">
        <div className="card space-y-2 p-5"><h3 className="font-bold">Status</h3><p className="text-xs text-slate-500">Automatic from dates unless you set it.</p>
          <select className="nselect" value={trip.status_override || ""} onChange={(e) => setStatus(e.target.value)} data-testid="trip-status-select">
            <option value="">Automatic ({trip.status.replace("_", " ")})</option><option value="planning">Planning</option><option value="upcoming">Upcoming</option><option value="in_progress">In progress</option><option value="completed">Completed</option></select></div>
        <GuideCard />
        <CardToggle />
        <Danger />
      </div>
    </div>
  );
}
